import { Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDividerModule } from '@angular/material/divider';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { AppState, Dialog, DialogFormActionsComponent } from '../common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DDSPreviewData, ProducerPreview } from '../model';
import { SiteDataBadgeComponent, WhispBadgeState } from '../common/geo/site-data-badge.component';
import { CompanyWriteAccess } from '../common/service/company-write-access.service';
import { SpeciesNamesComponent } from '../common/species-names.component';
import { producerSiteLink } from './producer-site-link';

/**
 * Result of the DDS-create confirmation dialog. `undefined`/falsy when cancelled.
 * `supplementaryUnitValue` carries the user-entered volume when the commodity requires one
 * (e.g. wood → m³ / `MTQ`); absent for kg-only commodities.
 */
export interface DdsCreationResult {
  supplementaryUnitValue?: number;
}

const WHISP_GAP_STATES: Record<string, WhispBadgeState> = {
  WHISP_PENDING: { status: 'PENDING' },
  WHISP_RISK: { status: 'RISK', unjustified: true },
  WHISP_TOO_LARGE: { status: 'ERROR', errorCode: 'TOO_LARGE_AREA' },
  WHISP_ERROR: { status: 'ERROR' },
};

@Component({
  templateUrl: 'traces-dds-confirm-submit-dialog.component.html',
  // A compact 32px icon button for the geodata rows. Shrinking it with inline width/height left
  // Material's padding (12px, from `.mat-mdc-icon-button.mat-mdc-button-base`, which also pins
  // the state-layer token to 48px) and the 24px mat-icon box in place, so the glyph overflowed
  // the 8px content box and sat ~4px right / ~9px low in its ripple. Padding = (32 - 18) / 2.
  styles: `
    .mat-mdc-icon-button.row-icon-button {
      --mat-icon-button-state-layer-size: 32px;
      --mat-icon-button-icon-size: 18px;
      width: 32px;
      height: 32px;
      padding: 7px;
      vertical-align: middle;
    }
    .row-icon-button mat-icon {
      width: 18px;
      height: 18px;
      font-size: 18px;
      line-height: 18px;
    }
  `,
  imports: [
    SpeciesNamesComponent,
    SiteDataBadgeComponent,
    MatDialogModule,
    MatButtonModule,
    MatToolbarModule,
    MatIconModule,
    MatTooltipModule,
    MatDividerModule,
    MatCheckboxModule,
    MatChipsModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    DialogFormActionsComponent,
    TranslateModule,
    RouterLink,
  ],
})
export class TracesDdsConfirmSubmitDialogComponent {
  readonly data = inject<{ previewData: DDSPreviewData }>(MAT_DIALOG_DATA);
  readonly dialog = inject(Dialog);
  /**
   * Belt and braces. The opener (`createDds()`) is already disabled for a read-only viewer, which
   * is the rule everywhere else in this sweep — but a DDS submission is an irreversible filing
   * with an outside authority, so this one dialog re-checks rather than trusting its caller.
   */
  private readonly writeAccess = inject(CompanyWriteAccess);
  private readonly dialogRef = inject(MatDialogRef<TracesDdsConfirmSubmitDialogComponent>);
  // No ActivatedRoute here (the dialog isn't routed) — the MANUAL-site shape needs the currently
  // selected company/site, the same source the surrounding traces screens read (D6).
  private readonly appState = inject(AppState);
  private readonly translate = inject(TranslateService);
  confirmed = false;

  /** The supplementary-unit descriptor from the preview, present only when one is required. */
  readonly supplementaryUnit = this.data.previewData.commodity.supplementaryUnit;
  /** User-entered value, seeded from the value stored on the product (if any). */
  volume = this.supplementaryUnit?.value ?? null;

  /** Human-readable unit derived from the EU TRACES qualifier token (e.g. MTQ → m³). */
  get unitLabel(): string {
    switch (this.supplementaryUnit?.qualifier) {
      case 'MTQ':
        return 'm³';
      default:
        return this.supplementaryUnit?.qualifier ?? '';
    }
  }

  get isVolumeValid(): boolean {
    if (!this.supplementaryUnit?.required) return true;
    return this.volume != null && this.volume > 0;
  }

  /**
   * `HSHeadingType` from the EUDR XSD — the pattern EU TRACES enforces on `hsHeading`
   * (2 to 6 digits, maxLength 6). A statement whose HS code does not match is rejected by
   * TRACES with its unmarshaller's raw JAXB message (hit in PROD 2026-09-04), so the dialog
   * says so up front instead of showing a silent `-`.
   *
   * Since 2026-09-12 it also gates [canSubmit]. This narrows the earlier "the dialog warns, it
   * does not block" rule (F1b, 2026-09-10), which was about not making the field *required*:
   * a draft may still be saved without an HS code. Submitting a non-matching one, though, is a
   * guaranteed server-side rejection, and letting the button stay live only turned it into an
   * IllegalArgumentException in a toast. The server guard in TracesDdsService.submit remains
   * the authority — this is the earlier, clearer refusal.
   */
  private static readonly HS_HEADING = /^[0-9]{2,6}$/;

  get hsCode(): string {
    return this.data.previewData.commodity.hsCode ?? '';
  }

  get isHsCodeValid(): boolean {
    return TracesDdsConfirmSubmitDialogComponent.HS_HEADING.test(this.hsCode);
  }

  /** True when the preview lists a producer row still missing its polygon, tree species or country. */
  get hasIncompleteProducer(): boolean {
    return this.data.previewData.commodity.geodata.some((p) => this.isIncomplete(p));
  }

  get hasWhispGap(): boolean {
    return this.whispGapProducers.length > 0;
  }

  get whispGapSiteNames(): string {
    return this.whispGapSites
      .map((p) => this.supplierName(p) ?? this.translate.instant('Supplier_tr'))
      .join(', ');
  }

  private get whispGapSites(): ProducerPreview[] {
    return this.whispGapProducers.filter((p, index, all) => all.findIndex((other) => other.siteId === p.siteId) === index);
  }

  private get whispGapProducers(): ProducerPreview[] {
    return this.data.previewData.commodity.geodata.filter((p) => !!p.whispGap);
  }

  protected whispGapState(producer: ProducerPreview): WhispBadgeState | undefined {
    return WHISP_GAP_STATES[producer.whispGap ?? ''];
  }

  /** Single row predicate (app#305) — shared by hasIncompleteProducer and the action cell. */
  protected isIncomplete(producer: ProducerPreview): boolean {
    return !producer.geoJson || !producer.species?.length || !producer.country;
  }

  /** Tooltip of a gap row's ⚠ (app#316): every missing field, in the server's check order. */
  protected gapTooltip(producer: ProducerPreview): string {
    const keys = [
      ...(producer.geoJson ? [] : ['producer_gap_geo_missing_tr']),
      ...(producer.species?.length ? [] : ['producer_gap_species_missing_tr']),
      ...(producer.country ? [] : ['producer_gap_country_missing_tr']),
    ];
    return keys.map((key) => this.translate.instant(key)).join(', ');
  }

  get canSubmit(): boolean {
    return (
      !this.writeAccess.readOnly() &&
      this.confirmed &&
      this.isVolumeValid &&
      this.isHsCodeValid &&
      !this.hasIncompleteProducer &&
      !this.hasWhispGap
    );
  }

  /** Same rule and shapes as ReportDetailChainArticleComponent.gapLink (D3/D6) — fail-closed. */
  protected gapLink(producer: ProducerPreview): unknown[] | null {
    return producerSiteLink(producer, this.appState.companySite$.value);
  }

  /** Non-null only when a name is actually present (D4). */
  protected supplierName(producer: ProducerPreview): string | null {
    return producer.name || null;
  }

  /** First missing field, in the server's own check order (D11) — the single ✗-row link can only focus one. */
  protected focusGap(producer: ProducerPreview): 'GEO_JSON' | 'EPPO_SPECIES' | 'COUNTRY' {
    if (!producer.geoJson) return 'GEO_JSON';
    if (!producer.species?.length) return 'EPPO_SPECIES';
    return 'COUNTRY';
  }

  submit(): void {
    if (!this.canSubmit) return;
    const result: DdsCreationResult = {};
    if (this.supplementaryUnit?.required && this.volume != null) {
      result.supplementaryUnitValue = this.volume;
    }
    this.dialogRef.close(result);
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
